<template>
  <div class="quota-page">
    <div class="page-head">
      <div>
        <h2>AI分组额度</h2>
        <p>维护生成用户分组、组长和视频生成费用额度</p>
      </div>
      <div class="head-actions">
        <el-button size="mini" icon="el-icon-refresh" :loading="loading || quotaLoading" @click="loadAll">刷新</el-button>
        <el-button v-if="currentUserCanAdmin" type="primary" size="mini" icon="el-icon-plus" @click="openCreateGroup">新建分组</el-button>
      </div>
    </div>

    <section class="panel toolbar-panel">
      <el-form :inline="true" size="mini" class="toolbar-form">
        <el-form-item label="月份">
          <el-date-picker
              v-model="periodStart"
              type="month"
              value-format="yyyy-MM-dd"
              placeholder="选择月份"
              style="width: 150px"
              @change="handleMonthChange">
          </el-date-picker>
        </el-form-item>
        <el-form-item>
          <el-tag size="mini" type="info">{{ scopeLabel }}</el-tag>
        </el-form-item>
      </el-form>
    </section>

    <section class="panel">
      <div class="panel-title">
        <span>分组额度</span>
        <small>{{ progress.periodStart || '-' }} 至 {{ progress.periodEnd || '-' }}</small>
      </div>
      <el-table
          :data="progress.groupRows || []"
          size="mini"
          border
          stripe
          v-loading="quotaLoading"
          :header-cell-style="{ background: '#f8fafc', color: '#303133', fontWeight: 600 }">
        <el-table-column prop="groupName" label="分组" min-width="160"></el-table-column>
        <el-table-column prop="leaderUsername" label="组长" width="130"></el-table-column>
        <el-table-column prop="memberCount" label="成员" width="80" align="right"></el-table-column>
        <el-table-column prop="quotaAmount" label="额度" width="130" align="right">
          <template slot-scope="{ row }">{{ formatCurrency(row.quotaAmount) }}</template>
        </el-table-column>
        <el-table-column prop="usedCost" label="已用" width="130" align="right">
          <template slot-scope="{ row }">{{ formatCurrency(row.usedCost) }}</template>
        </el-table-column>
        <el-table-column label="进度" min-width="220">
          <template slot-scope="{ row }">
            <div class="progress-cell">
              <el-progress :percentage="progressPercent(row)" :status="progressStatus(row)" :stroke-width="10"></el-progress>
              <span>{{ usageText(row) }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="taskCount" label="任务" width="80" align="right"></el-table-column>
        <el-table-column prop="chargedTaskCount" label="计费" width="80" align="right"></el-table-column>
        <el-table-column prop="usageTokens" label="Tokens" width="130" align="right">
          <template slot-scope="{ row }">{{ formatInteger(row.usageTokens) }}</template>
        </el-table-column>
        <el-table-column prop="generatedDurationSeconds" label="视频时长" width="110" align="right">
          <template slot-scope="{ row }">{{ formatDuration(row.generatedDurationSeconds) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="150" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="mini" @click="openQuotaDialog(row)">额度</el-button>
            <el-button v-if="currentUserCanAdmin" type="text" size="mini" @click="openEditGroup(row)">编辑</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-empty v-if="!quotaLoading && !(progress.groupRows || []).length" description="暂无可查看分组"></el-empty>
    </section>

    <section class="panel">
      <div class="panel-title">
        <span>成员额度进度</span>
        <small>组长可查看组内成员</small>
      </div>
      <el-table
          :data="progress.memberRows || []"
          size="mini"
          border
          stripe
          v-loading="quotaLoading"
          max-height="420"
          :header-cell-style="{ background: '#f8fafc', color: '#303133', fontWeight: 600 }">
        <el-table-column prop="groupName" label="分组" min-width="150"></el-table-column>
        <el-table-column prop="username" label="成员" min-width="130"></el-table-column>
        <el-table-column prop="memberRole" label="角色" width="90">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="row.memberRole === 'LEADER' ? 'warning' : 'info'">{{ row.memberRole === 'LEADER' ? '组长' : '成员' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="quotaAmount" label="额度" width="130" align="right">
          <template slot-scope="{ row }">{{ formatCurrency(row.quotaAmount) }}</template>
        </el-table-column>
        <el-table-column prop="usedCost" label="已用" width="130" align="right">
          <template slot-scope="{ row }">{{ formatCurrency(row.usedCost) }}</template>
        </el-table-column>
        <el-table-column label="进度" min-width="220">
          <template slot-scope="{ row }">
            <div class="progress-cell">
              <el-progress :percentage="progressPercent(row)" :status="progressStatus(row)" :stroke-width="10"></el-progress>
              <span>{{ usageText(row) }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="taskCount" label="任务" width="80" align="right"></el-table-column>
        <el-table-column prop="usageTokens" label="Tokens" width="130" align="right">
          <template slot-scope="{ row }">{{ formatInteger(row.usageTokens) }}</template>
        </el-table-column>
        <el-table-column prop="generatedDurationSeconds" label="视频时长" width="110" align="right">
          <template slot-scope="{ row }">{{ formatDuration(row.generatedDurationSeconds) }}</template>
        </el-table-column>
      </el-table>
    </section>

    <el-dialog
        :title="groupForm.id ? '编辑分组' : '新建分组'"
        :visible.sync="groupDialogVisible"
        width="720px"
        :close-on-click-modal="false">
      <el-form size="mini" label-width="86px">
        <el-form-item label="分组名称" required>
          <el-input v-model.trim="groupForm.groupName" placeholder="例如：视频生成一组"></el-input>
        </el-form-item>
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="组长用户名" required>
              <el-input v-model.trim="groupForm.leaderUsername" placeholder="按任务创建人用户名匹配"></el-input>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="组长ID">
              <el-input v-model.trim="groupForm.leaderUserId" placeholder="可选"></el-input>
            </el-form-item>
          </el-col>
        </el-row>
        <div class="dialog-sub-title">
          <span>组员</span>
          <el-button type="text" size="mini" icon="el-icon-plus" @click="addGroupMember">添加组员</el-button>
        </div>
        <div v-for="(member, index) in groupForm.members" :key="index" class="member-edit-row">
          <el-input v-model.trim="member.username" size="mini" placeholder="用户名"></el-input>
          <el-input v-model.trim="member.userId" size="mini" placeholder="用户ID（可选）"></el-input>
          <el-button type="text" size="mini" icon="el-icon-delete" class="danger-link" @click="removeGroupMember(index)"></el-button>
        </div>
      </el-form>
      <span slot="footer">
        <el-button size="mini" @click="groupDialogVisible = false">取消</el-button>
        <el-button type="primary" size="mini" :loading="savingGroup" @click="saveGroup">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog
        title="额度分配"
        :visible.sync="quotaDialogVisible"
        width="760px"
        :close-on-click-modal="false">
      <div class="quota-dialog-head">
        <strong>{{ quotaForm.groupName }}</strong>
        <span>{{ progress.periodStart || '-' }} 至 {{ progress.periodEnd || '-' }}</span>
      </div>
      <el-form size="mini" label-width="92px">
        <el-form-item label="组额度">
          <el-input-number
              v-model="quotaForm.groupQuotaAmount"
              :min="0"
              :precision="2"
              :step="100"
              :disabled="!currentUserCanAdmin"
              controls-position="right"
              style="width: 220px">
          </el-input-number>
          <span class="form-hint">管理员可调整组额度</span>
        </el-form-item>
      </el-form>
      <div class="quota-limit-summary" :class="{ exceeded: quotaExceeded }">
        <span>成员额度合计：{{ formatCurrency(totalMemberQuota) }}</span>
        <span>剩余额度：{{ formatCurrency(remainingMemberQuota) }}</span>
        <span v-if="quotaExceeded">已超过组额度，不能保存</span>
      </div>
      <el-table
          :data="quotaForm.memberQuotas"
          size="mini"
          border
          stripe
          max-height="380"
          :header-cell-style="{ background: '#f8fafc', color: '#303133', fontWeight: 600 }">
        <el-table-column prop="username" label="成员" min-width="160"></el-table-column>
        <el-table-column prop="usedCost" label="已用" width="120" align="right">
          <template slot-scope="{ row }">{{ formatCurrency(row.usedCost) }}</template>
        </el-table-column>
        <el-table-column label="成员额度" width="220">
          <template slot-scope="{ row }">
            <el-input-number
                v-model="row.quotaAmount"
                :min="0"
                :max="memberQuotaMax(row)"
                :precision="2"
                :step="50"
                controls-position="right"
                style="width: 180px">
            </el-input-number>
          </template>
        </el-table-column>
        <el-table-column label="进度" min-width="180">
          <template slot-scope="{ row }">
            <el-progress :percentage="progressPercent(row)" :status="progressStatus(row)" :stroke-width="8"></el-progress>
          </template>
        </el-table-column>
      </el-table>
      <span slot="footer">
        <el-button size="mini" @click="quotaDialogVisible = false">取消</el-button>
        <el-button type="primary" size="mini" :loading="savingQuota" :disabled="quotaExceeded" @click="saveQuotas">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  getAigcQuotaProgress,
  listAigcUserGroups,
  saveAigcUserGroup,
  saveAigcUserGroupQuotas
} from '@/api/aigcVideo'

export default {
  name: 'AigcQuotaGroupPage',
  data() {
    const periodPreference = this.loadPeriodPreference()
    return {
      loading: false,
      quotaLoading: false,
      savingGroup: false,
      savingQuota: false,
      groups: [],
      progress: {
        periodType: 'MONTH',
        periodStart: '',
        periodEnd: '',
        groupRows: [],
        memberRows: []
      },
      periodType: 'MONTH',
      periodStart: periodPreference.periodStart,
      groupDialogVisible: false,
      quotaDialogVisible: false,
      groupForm: this.defaultGroupForm(),
      quotaForm: this.defaultQuotaForm()
    }
  },
  computed: {
    currentUserCanAdmin() {
      return this.currentUserIsAdmin()
    },
    scopeLabel() {
      return this.currentUserCanAdmin ? '管理员可查看全部分组' : '组长可查看本组，成员可查看本人额度'
    },
    groupQuotaAmount() {
      return Math.max(Number(this.quotaForm.groupQuotaAmount || 0), 0)
    },
    totalMemberQuota() {
      return (this.quotaForm.memberQuotas || []).reduce((total, item) => {
        return total + Math.max(Number(item.quotaAmount || 0), 0)
      }, 0)
    },
    remainingMemberQuota() {
      return this.groupQuotaAmount - this.totalMemberQuota
    },
    quotaExceeded() {
      return this.totalMemberQuota > this.groupQuotaAmount
    }
  },
  mounted() {
    this.loadAll()
  },
  methods: {
    defaultGroupForm() {
      return {
        id: null,
        groupName: '',
        leaderUserId: '',
        leaderUsername: '',
        members: [{ username: '', userId: '' }]
      }
    },
    defaultQuotaForm() {
      return {
        groupId: null,
        groupName: '',
        groupQuotaAmount: 0,
        memberQuotas: []
      }
    },
    async loadAll() {
      await Promise.all([this.loadGroups(), this.loadProgress()])
    },
    async loadGroups() {
      this.loading = true
      try {
        const response = await listAigcUserGroups()
        this.groups = response.data || []
      } catch (error) {
        this.groups = []
        this.$message.error(error.message || '获取分组失败')
      } finally {
        this.loading = false
      }
    },
    async loadProgress() {
      this.periodType = 'MONTH'
      this.periodStart = this.monthStart(this.periodStart || this.todayDate())
      this.savePeriodPreference()
      this.quotaLoading = true
      try {
        const response = await getAigcQuotaProgress({
          periodType: this.periodType,
          periodStart: this.periodStart
        })
        this.progress = Object.assign({
          periodType: this.periodType,
          periodStart: this.periodStart,
          periodEnd: '',
          groupRows: [],
          memberRows: []
        }, response.data || {})
      } catch (error) {
        this.progress = {
          periodType: this.periodType,
          periodStart: this.periodStart,
          periodEnd: '',
          groupRows: [],
          memberRows: []
        }
        this.$message.error(error.message || '获取额度进度失败')
      } finally {
        this.quotaLoading = false
      }
    },
    handleMonthChange() {
      this.periodType = 'MONTH'
      this.periodStart = this.monthStart(this.periodStart || this.todayDate())
      this.savePeriodPreference()
      this.loadProgress()
    },
    openCreateGroup() {
      this.groupForm = this.defaultGroupForm()
      this.groupDialogVisible = true
    },
    openEditGroup(row) {
      const group = this.groups.find(item => item.id === row.groupId) || {}
      this.groupForm = {
        id: group.id,
        groupName: group.groupName || row.groupName || '',
        leaderUserId: group.leaderUserId || '',
        leaderUsername: group.leaderUsername || row.leaderUsername || '',
        members: (group.members || []).map(item => ({
          userId: item.userId || '',
          username: item.username || ''
        }))
      }
      if (!this.groupForm.members.length) {
        this.groupForm.members.push({ username: '', userId: '' })
      }
      this.groupDialogVisible = true
    },
    addGroupMember() {
      this.groupForm.members.push({ username: '', userId: '' })
    },
    removeGroupMember(index) {
      this.groupForm.members.splice(index, 1)
      if (!this.groupForm.members.length) {
        this.addGroupMember()
      }
    },
    async saveGroup() {
      if (!this.groupForm.groupName || !this.groupForm.leaderUsername) {
        this.$message.warning('请填写分组名称和组长用户名')
        return
      }
      this.savingGroup = true
      try {
        await saveAigcUserGroup({
          id: this.groupForm.id,
          groupName: this.groupForm.groupName,
          leaderUserId: this.groupForm.leaderUserId,
          leaderUsername: this.groupForm.leaderUsername,
          members: this.groupForm.members
              .map(item => ({
                userId: String(item.userId || '').trim(),
                username: String(item.username || '').trim()
              }))
              .filter(item => item.userId || item.username)
        })
        this.$message.success('分组已保存')
        this.groupDialogVisible = false
        await this.loadAll()
      } catch (error) {
        this.$message.error(error.message || '保存分组失败')
      } finally {
        this.savingGroup = false
      }
    },
    openQuotaDialog(row) {
      const group = this.groups.find(item => item.id === row.groupId) || {}
      const memberProgress = this.memberProgressMap(row.groupId)
      this.quotaForm = {
        groupId: row.groupId,
        groupName: row.groupName,
        groupQuotaAmount: Number(row.quotaAmount || 0),
        memberQuotas: (group.members || []).map(member => {
          const key = this.memberKey(member)
          const progress = memberProgress[key] || {}
          return {
            userId: member.userId || '',
            username: member.username || '',
            usedCost: Number(progress.usedCost || 0),
            quotaAmount: Number(progress.quotaAmount || 0),
            usageRate: Number(progress.usageRate || 0),
            overQuota: !!progress.overQuota
          }
        })
      }
      this.quotaDialogVisible = true
    },
    async saveQuotas() {
      if (!this.quotaForm.groupId) {
        return
      }
      if (this.quotaExceeded) {
        this.$message.warning('成员额度合计不能超过组额度')
        return
      }
      this.savingQuota = true
      try {
        await saveAigcUserGroupQuotas(this.quotaForm.groupId, {
          periodType: this.periodType,
          periodStart: this.periodStart,
          groupQuotaAmount: this.quotaForm.groupQuotaAmount,
          memberQuotas: this.quotaForm.memberQuotas.map(item => ({
            userId: item.userId,
            username: item.username,
            quotaAmount: item.quotaAmount
          }))
        })
        this.$message.success('额度已保存')
        this.quotaDialogVisible = false
        await this.loadProgress()
      } catch (error) {
        this.$message.error(error.message || '保存额度失败')
      } finally {
        this.savingQuota = false
      }
    },
    memberProgressMap(groupId) {
      const result = {}
      ;(this.progress.memberRows || [])
          .filter(item => item.groupId === groupId)
          .forEach(item => {
            result[this.memberKey(item)] = item
          })
      return result
    },
    memberKey(member) {
      return member.username ? `name:${member.username}` : `id:${member.userId || ''}`
    },
    memberQuotaMax(row) {
      const current = Math.max(Number(row && row.quotaAmount ? row.quotaAmount : 0), 0)
      const usedByOthers = Math.max(this.totalMemberQuota - current, 0)
      return Math.max(this.groupQuotaAmount - usedByOthers, 0)
    },
    loadPeriodPreference() {
      const fallback = {
        periodType: 'MONTH',
        periodStart: this.monthStart(this.todayDate())
      }
      try {
        const saved = JSON.parse(localStorage.getItem(this.periodPreferenceKey()) || '{}')
        return {
          periodType: fallback.periodType,
          periodStart: this.isDateString(saved.periodStart) ? this.monthStart(saved.periodStart) : fallback.periodStart
        }
      } catch (e) {
        return fallback
      }
    },
    savePeriodPreference() {
      try {
        localStorage.setItem(this.periodPreferenceKey(), JSON.stringify({
          periodType: 'MONTH',
          periodStart: this.monthStart(this.periodStart || this.todayDate())
        }))
      } catch (e) {
        // localStorage may be unavailable in private or restricted browser contexts.
      }
    },
    periodPreferenceKey() {
      const user = this.currentUser()
      const userKey = this.firstDefined(user.username, user.userName, user.id, user.userId, user.user_id) || 'default'
      return `aigc_quota_period_preference:${userKey}`
    },
    isDateString(value) {
      return /^\d{4}-\d{2}-\d{2}$/.test(String(value || ''))
    },
    todayDate() {
      return this.formatDate(new Date())
    },
    monthStart(value) {
      const text = String(value || '').trim()
      if (/^\d{4}-\d{2}/.test(text)) {
        return text.substring(0, 7) + '-01'
      }
      return this.formatDate(new Date())
    },
    currentUser() {
      try {
        return JSON.parse(localStorage.getItem('user') || '{}')
      } catch (e) {
        return {}
      }
    },
    currentUserIsAdmin() {
      const user = this.currentUser()
      const accountType = this.firstDefined(user.accountType, user.account_type, user.userType, user.user_type, user.type)
      const role = String(user.role || user.userRole || '').toLowerCase()
      const username = String(user.username || '').toLowerCase()
      return String(accountType) === '1'
          || this.isTrue(user.admin)
          || this.isTrue(user.isAdmin)
          || role === 'admin'
          || role === 'administrator'
          || username === 'admin'
          || username === 'administrator'
    },
    firstDefined() {
      for (let i = 0; i < arguments.length; i += 1) {
        const value = arguments[i]
        if (value !== undefined && value !== null && value !== '') {
          return value
        }
      }
      return undefined
    },
    isTrue(value) {
      return value === true || value === 1 || String(value).toLowerCase() === 'true'
    },
    formatCurrency(value) {
      const number = Number(value || 0)
      return '￥' + number.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatInteger(value) {
      return Number(value || 0).toLocaleString('zh-CN')
    },
    formatDuration(seconds) {
      const value = Math.max(Number(seconds || 0), 0)
      const hours = Math.floor(value / 3600)
      const minutes = Math.floor((value % 3600) / 60)
      const secs = Math.floor(value % 60)
      if (hours > 0) {
        return `${hours}小时${minutes}分`
      }
      if (minutes > 0) {
        return `${minutes}分${secs}秒`
      }
      return `${secs}秒`
    },
    progressPercent(row) {
      const percent = Number(row && row.usageRate ? row.usageRate : 0)
      return Math.max(0, Math.min(100, Number(percent.toFixed(2))))
    },
    progressStatus(row) {
      if (row && row.overQuota) {
        return 'exception'
      }
      return this.progressPercent(row) >= 80 ? 'warning' : 'success'
    },
    usageText(row) {
      const quota = Number(row && row.quotaAmount ? row.quotaAmount : 0)
      if (quota <= 0) {
        return '未设置额度'
      }
      return `${this.progressPercent(row)}%`
    },
    formatDate(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
  }
}
</script>

<style scoped>
.quota-page {
  min-height: 100%;
  padding: 18px;
  background: #f5f7fb;
  color: #303133;
  box-sizing: border-box;
}

.page-head,
.panel-title,
.dialog-sub-title,
.quota-dialog-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-head {
  margin-bottom: 14px;
}

.page-head h2 {
  margin: 0;
  font-size: 22px;
  line-height: 30px;
}

.page-head p,
.panel-title small,
.form-hint {
  margin: 4px 0 0;
  color: #8c98a8;
  font-size: 12px;
}

.head-actions {
  display: flex;
  gap: 8px;
}

.panel {
  background: #fff;
  border: 1px solid #e6ebf2;
  border-radius: 6px;
  padding: 14px;
  margin-bottom: 14px;
}

.toolbar-panel {
  padding-bottom: 4px;
}

.toolbar-form {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.panel-title {
  margin-bottom: 12px;
  font-weight: 700;
}

.progress-cell {
  display: grid;
  grid-template-columns: minmax(120px, 1fr) 72px;
  align-items: center;
  gap: 10px;
}

.member-edit-row {
  display: grid;
  grid-template-columns: 1fr 1fr 36px;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}

.dialog-sub-title {
  margin: 6px 0 10px;
  font-weight: 700;
}

.quota-dialog-head {
  margin-bottom: 12px;
}

.quota-dialog-head span {
  color: #8c98a8;
  font-size: 12px;
}

.quota-limit-summary {
  display: flex;
  gap: 16px;
  align-items: center;
  margin: 0 0 10px 92px;
  color: #606266;
  font-size: 12px;
}

.quota-limit-summary.exceeded {
  color: #f56c6c;
}

.danger-link {
  color: #f56c6c;
}
</style>
